import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Text } from '@/nova/components';
import { rangeFromPreset } from '@/nova/pages/affiliates/reports-helpers';

import { playersFromCampaignPath } from '../../link-attribution';
import { useCampaignStats } from '../../useCampaignStats';
import { sumMetricField } from '../../video-stats';
import { openInApp } from '../LandingPageComposer/link-for';

import { Line, PlayersLink, Wrap } from './TrackingLinkStats.styles';

export interface TrackingLinkStatsProps {
  /** The link's `campaign` value (what the stats data set reports its sign-ups against). */
  campaign: string;
}

/**
 * The shown link's results over the last 30 days (registrations, FTDs, deposits), from the Owners Hub Stats data set
 * the link's `aff_id` feeds. Nothing until the read succeeds; zeros are real (a link nobody used yet). Once someone
 * registered through it, "See players" opens the Players page filtered to this link's players.
 */
export const TrackingLinkStats = ({ campaign }: TrackingLinkStatsProps): React.ReactElement | null => {
  const navigate = useNavigate();
  // The window is fixed when the panel opens.
  const [range] = React.useState(() => rangeFromPreset('30d', new Date()));
  const campaigns = React.useMemo(() => [campaign], [campaign]);
  const { status, metricsList } = useCampaignStats({ campaigns, range });
  if (status !== 'ok') return null;
  const registrations = sumMetricField(metricsList, 'registrations');
  const playersPath = playersFromCampaignPath(campaign);
  return (
    <Wrap>
      <Line
        text="Last 30 days: {{registrations}} registrations · {{ftds}} FTDs · {{deposits}} deposits"
        placeholders={{
          registrations,
          ftds: sumMetricField(metricsList, 'ftds'),
          deposits: sumMetricField(metricsList, 'deposits'),
        }}
      />
      {registrations > 0 ? (
        <PlayersLink href={playersPath} onClick={(e) => openInApp(e, playersPath, (path) => navigate(path))}>
          <Text text="See players" tag="span" />
        </PlayersLink>
      ) : null}
    </Wrap>
  );
};

export default TrackingLinkStats;
